import { type ProgrameeTyping, type TimeslotTyping } from '@/typings/widget';
import { DateTime } from '@shared/helpers';

/**
 * Calculates the offset, in minutes, between a timeslot's original start time and
 * a desired clock time ("HH:mm"). The offset is positive when the desired time is
 * later than the original and negative when it is earlier.
 *
 * @param startDateTime - The original timeslot start datetime (ISO string)
 * @param startTime - The desired start time in "HH:mm" format
 * @returns The difference in minutes between the desired and original start times
 */
const getStartTimeOffsetMinutes = (startDateTime: string, startTime: string): number => {
    const [hours, minutes] = startTime.split(':')
        .map(Number);
    const original = DateTime.dayjsObject(startDateTime);
    const target = original
        .hour(hours)
        .minute(minutes)
        .second(0)
        .millisecond(0);

    return target.diff(original, 'minute');
};

/**
 * Shifts every timeslot of a single day by a fixed number of minutes. Each
 * timeslot keeps its own duration and the gaps between consecutive timeslots are
 * preserved, so only the absolute clock times move.
 *
 * @param timeslots - The day's timeslots to shift
 * @param offsetMinutes - The amount, in minutes, to shift each timeslot by
 * @returns A new array of timeslots with shifted start/end datetimes
 */
const shiftDayTimeslots = (timeslots: TimeslotTyping[], offsetMinutes: number): TimeslotTyping[] =>
    timeslots.map(timeslot => ({
        ...timeslot,
        startDateTime: DateTime.dayjsObject(timeslot.startDateTime)
            .add(offsetMinutes, 'minute')
            .format(),
        endDateTime: DateTime.dayjsObject(timeslot.endDateTime)
            .add(offsetMinutes, 'minute')
            .format(),
    }));

/**
 * Recalculates a package programme so its FIRST DAY starts at the selected filter
 * start time. The first timeslot of the first day is anchored to the chosen time,
 * and every other timeslot of that day is shifted by the same offset so durations
 * and gaps stay intact. All remaining days of a multi-day package are left
 * untouched.
 *
 * @param programme - The original package programme (days with timeslots)
 * @param startTime - The selected start time in "HH:mm" format
 * @returns A new programme with the first day shifted, or the original programme
 *          when there is nothing to recalculate
 */
export const applyStartTimeFilterToProgramme = (
    programme: ProgrameeTyping[],
    startTime: string,
): ProgrameeTyping[] => {
    // Nothing to recalculate without a selected start time or an actual programme.
    if (!startTime || !programme?.length) return programme;

    const [firstDay, ...restDays] = programme;
    const firstTimeslot = firstDay.timeslots?.[0];

    // A reference timeslot on the first day is needed to anchor the offset.
    if (!firstTimeslot?.startDateTime) return programme;

    const offsetMinutes = getStartTimeOffsetMinutes(firstTimeslot.startDateTime, startTime);

    // The selected time already matches the original start, so no shift is needed.
    if (!offsetMinutes) return programme;

    return [
        {
            ...firstDay,
            timeslots: shiftDayTimeslots(firstDay.timeslots, offsetMinutes),
        },
        ...restDays,
    ];
};
